// MODELUL ÎNTREGULUI COMPLEX — vederea «Toate corpurile».
//
// Fiecare corp (tronson) al unei soluții e analizat separat, în cadrul lui, și
// desenat singur. Pe «Toate corpurile» Studioul cere serverului un singur
// model cu toate corpurile puse unde stau pe parcelă, peretele exterior al
// parcării și groapa pe tot subsolul (`steps/model_fem/complex`,
// `structure/complex_corpuri.py`). E o GEOMETRIE, nu o analiză: verdictele și
// armarea rămân ale fiecărui corp, pe pagina lui.
//
// Protocolul e al schiței FEM (`schitaFem.ts`): serverul răspunde 200 din
// cache sau 202 «se construiește» și construiește în fundal; aici se revine
// la câteva secunde până vine modelul.

import { useEffect, useState } from "react";

import { getModelComplex } from "./apiStructura";
import type { FemResult } from "./tipuri";

export type StareComplex = "inactiva" | "se_incarca" | "gata" | "lipsa";

/** Construcția ține câteva secunde pe un complex cu modelele gata și zeci
 *  când corpurilor li se construiesc schițele FEM; nici prea des, nici prea
 *  rar. */
const REINCERCARE_MS = 4000;

/** Modelul complexului soluției `solutie`, cât `activ` e adevărat.
 *
 *  `inactiva` = nu e nevoie de el (nu se stă pe «Toate corpurile»), deci nu
 *  se cere nimic; `lipsa` = serverul n-are ce construi (soluție fără corpuri,
 *  niciun corp cu Pasul 4) sau a căzut. `versiune` schimbă cheia cererii:
 *  cu alta se cere din nou, iar ETag-ul serverului face cererea ieftină. */
export function useModelComplex(
  projectId: string,
  solutie: string | null | undefined,
  activ: boolean,
  versiune?: string | null,
): { model: FemResult | null; stare: StareComplex } {
  const [model, setModel] = useState<FemResult | null>(null);
  const [stare, setStare] = useState<StareComplex>("inactiva");
  const cerem = activ && !!solutie;

  useEffect(() => {
    if (!cerem) {
      setModel(null);
      setStare("inactiva");
      return;
    }
    let renunta = false;
    let ceas: ReturnType<typeof setTimeout> | undefined;
    setStare("se_incarca");
    const cere = () => {
      getModelComplex(projectId, solutie!, versiune)
        .then((r) => {
          if (renunta) return;
          if (r?.se_construieste || !r?.model) {
            ceas = setTimeout(cere, REINCERCARE_MS);
            return;
          }
          setModel(r as unknown as FemResult);
          setStare("gata");
        })
        .catch(() => {
          if (renunta) return;
          setModel(null);
          setStare("lipsa");
        });
    };
    cere();
    return () => { renunta = true; if (ceas) clearTimeout(ceas); };
  }, [projectId, solutie, versiune, cerem]);

  return { model, stare };
}
